import * as Redux from 'redux';
import { Dispatch } from 'react-redux';
import { ReportsState, ReportAdministrationView } from '../ReportsState';
import { SSRSCredential, Query } from '../../misc/api/apiTypes';
import { Folder } from '../../misc/api/ReportsApiTypes';
import { InitializeReportsAsyncAction } from '../ReportActions';
import { MainState } from '../../Core/reducers';
import { WebPortalLegacyClientInternal } from '../../misc/api/WebPortalTokenClient';
import { Role, WebPortalClient } from 'webportalclient';

declare const rootUrl: string;

export enum AdministrationActionType {
    ChangeView = 'ChangeView',
    FailedToConnectToReportServer = 'FailedToConnectToReportServer',
    ConnectedToReportServer = 'ConnectedToReportServer',
    ReceiveCredentials = 'ReceiveCredentials',
    ReceiveFolders = 'ReceiveFolders',
    ToggleReportServerConnectionVisibility = 'ToggleReportServerConnectionVisibility',
    ToggleReportRolesVisibility = 'ToggleReportRolesVisibility',
    ReceiveReportRoles = 'ReceiveReportRoles',
    SetActiveReport = 'SetActiveReport',
    DoneUpsertingReportRoles = 'DoneUpsertingReportRoles',
    ToggleImportBaseReportsModalVisibility = 'ToggleImportBaseReportsModalVisibility',
    SetSelectedRootFolder = 'SetSelectedRootFolder',
    OtherAction = '_OtherAction_',
}

export class TestConnectionAsyncAction {
    static create(legacyClient: WebPortalLegacyClientInternal) {
        return async (dispatch: Dispatch<MainState>) => {
            const success = await legacyClient.testStoredConnection();

            if (success) {
                dispatch(ConnectedToReportServerAction.create());
                dispatch(InitializeReportsAsyncAction.create(legacyClient));
            } else {
                dispatch(FailedToConnectToReportServerAction.create('Failed to connect to the Report Server. Please check your credentials'));
                dispatch(ChangeViewAction.create(ReportAdministrationView.Credentials));
            }
        }
    }
}

export class SetSelectedRootFolderAction implements Redux.Action {
    type: AdministrationActionType.SetSelectedRootFolder;
    selectedRootFolder: string;

    static create(selectedRootFolder: string): SetSelectedRootFolderAction {
        return {
            type: AdministrationActionType.SetSelectedRootFolder,
            selectedRootFolder: selectedRootFolder
        }
    }
}

export class GetSelectedRootFolderAction {
    static create(legacyClient: WebPortalLegacyClientInternal) {
        return (dispatch: Dispatch<MainState>) => {
            legacyClient.getStudySelectedRootFolder().then(selectedFolder => dispatch(SetSelectedRootFolderAction.create(selectedFolder)));
        }
    }
}

export class UpdateReportServerCredentialsAsyncAction {
    static create(legacyClient: WebPortalLegacyClientInternal, ssrsCredential: SSRSCredential) {
        return (dispatch: Dispatch<MainState>, getState: () => MainState) => {
            dispatch(ChangeViewAction.create(ReportAdministrationView.Loading));
            legacyClient.putSetting("Vitalograph.WebPortal.Data.Settings.Reports.SSRSCredentialsSetting", JSON.stringify(ssrsCredential))
                .then(async json => {
                    if (json.Errors) {
                        dispatch(FailedToConnectToReportServerAction.create(json.Errors[0].Message));
                    } else {
                        dispatch(GetCredentialsAsyncAction.create(legacyClient))
                        dispatch(TestConnectionAsyncAction.create(legacyClient));
                        dispatch(ChangeViewAction.create(ReportAdministrationView.Hidden));
                    }
                });
        }
    }

    static async testConnection(legacyClient: WebPortalLegacyClientInternal, dispatch: Dispatch<MainState>) {
        const success = await legacyClient.testStoredConnection();
        if (success) {
            dispatch(ChangeViewAction.create(ReportAdministrationView.RootFolder));
        } else {
            dispatch(FailedToConnectToReportServerAction.create('Failed to connect to the Report Server. Please check your credentials'));
            dispatch(ChangeViewAction.create(ReportAdministrationView.Credentials));
        }

        return success;
    }
}

export class ChangeViewAction implements Redux.Action {
    type: AdministrationActionType.ChangeView;
    view: ReportAdministrationView;

    static create(view: ReportAdministrationView): ChangeViewAction {
        return {
            type: AdministrationActionType.ChangeView,
            view: view,
        }
    }
}

export class ConnectedToReportServerAction implements Redux.Action {
    type: AdministrationActionType.ConnectedToReportServer;

    static create(): ConnectedToReportServerAction {
        return {
            type: AdministrationActionType.ConnectedToReportServer,
        }
    }
}

export class FailedToConnectToReportServerAction implements Redux.Action {
    type: AdministrationActionType.FailedToConnectToReportServer;
    errorMessage: string;

    static create(errorMessage: string): FailedToConnectToReportServerAction {
        return {
            type: AdministrationActionType.FailedToConnectToReportServer,
            errorMessage: errorMessage,
        }
    }
}

export class UpdateReportServerRootFolderAsyncAction {
    static create(legacyClient: WebPortalLegacyClientInternal, rootFolderId: string, studyId: string) {
        return (dispatch: Dispatch<MainState>) => {
            dispatch(ChangeViewAction.create(ReportAdministrationView.Loading));
            legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Reports.SSRSRootFolderSetting", rootFolderId)
                .then(json => {
                    dispatch(TestConnectionAsyncAction.create(legacyClient));
                    dispatch(ChangeViewAction.create(ReportAdministrationView.Hidden))
                })
                .then(() => dispatch(SetSelectedRootFolderAction.create(rootFolderId)));
        }
    }
}

export class GetCredentialsAsyncAction {
    static create(legacyClient: WebPortalLegacyClientInternal) {
        return dispatch => {
            legacyClient.getReportCredentials()
                .then(json => {
                    if (json === null) {
                        //dispatch()
                    }
                    dispatch(ReceiveCredentialsAction.create(json))
                });

        };
    }
}

export class ReceiveCredentialsAction implements Redux.Action {
    type: AdministrationActionType.ReceiveCredentials;
    credentials: SSRSCredential;

    static create(credentials: SSRSCredential): ReceiveCredentialsAction {
        return {
            type: AdministrationActionType.ReceiveCredentials,
            credentials: credentials,
        }
    }
}

export class GetFoldersAsyncAction {
    static create(legacyClient: WebPortalLegacyClientInternal, ssrsCredential: SSRSCredential) {
        return async dispatch => {
            const json = await legacyClient.getFolders(ssrsCredential.ServerURL, ssrsCredential.Username, null, ssrsCredential.PasswordEncrypted);
            if (json) {
                dispatch(ReceiveFoldersAction.create(json))
                dispatch(ChangeViewAction.create(ReportAdministrationView.RootFolder));
            } else {
                dispatch(FailedToConnectToReportServerAction.create('Failed to connect to the Report Server. Please check your credentials'));
                dispatch(ChangeViewAction.create(ReportAdministrationView.Credentials));
            }
        };
    }
}

export class ReceiveFoldersAction implements Redux.Action {
    type: AdministrationActionType.ReceiveFolders;
    folders: Query<Folder>;

    static create(json: Query<Folder>): ReceiveFoldersAction {
        return {
            type: AdministrationActionType.ReceiveFolders,
            folders: json,
        }
    }
}

export class ToggleReportServerConnectionVisibilityAction implements Redux.Action {
    type: AdministrationActionType.ToggleReportServerConnectionVisibility;

    static create(): ToggleReportServerConnectionVisibilityAction {
        return {
            type: AdministrationActionType.ToggleReportServerConnectionVisibility,
        }
    }
}

export class AssignReportRolesAsyncAction {
    static create(reportId: string, legacyClient: WebPortalLegacyClientInternal) {
        return (dispatch: Dispatch<MainState>) => {
            legacyClient.getReportParameters(reportId)
                .then(json => {
                    dispatch(SetActiveReportAction.create(reportId));
                    dispatch(ToggleReportRolesVisiblityAction.create());
                });
        }
    }
}

export class ToggleReportRolesVisiblityAction implements Redux.Action {
    type: AdministrationActionType.ToggleReportRolesVisibility;

    static create(): ToggleReportRolesVisiblityAction {
        return {
            type: AdministrationActionType.ToggleReportRolesVisibility,
        }
    }
}

export class SetActiveReportAction implements Redux.Action {
    type: AdministrationActionType.SetActiveReport;
    reportId: string;

    static create(reportId: string): SetActiveReportAction {
        return {
            type: AdministrationActionType.SetActiveReport,
            reportId: reportId,
        }
    }
}

export class GetReportRolesAsyncAction {
    static create(client: WebPortalClient) {
        return (dispatch: Dispatch<MainState>) => {
            client.getRoles()
                .then(json => dispatch(ReceiveReportRolesAction.create(json.value)));
        }
    }
}

export class ReceiveReportRolesAction implements Redux.Action {
    type: AdministrationActionType.ReceiveReportRoles;
    roles: Role[];

    static create(roles: Role[]): ReceiveReportRolesAction {
        return {
            type: AdministrationActionType.ReceiveReportRoles,
            roles: roles,
        }
    }
}

export class UpsertReportRolesAsyncAction {
    static create(legacyClient: WebPortalLegacyClientInternal, reportId: string, roles: Array<any>) {
        return (dispatch: Dispatch<MainState>) => {
            legacyClient.getSSRSReportRoles(reportId, roles)
                .then(json => {
                    dispatch(InitializeReportsAsyncAction.create(legacyClient))
                    dispatch(ToggleReportRolesVisiblityAction.create())
                })
        }
    }
}

export class DoneUpsertingReportRoles implements Redux.Action {
    type: AdministrationActionType.DoneUpsertingReportRoles;

    static create(): DoneUpsertingReportRoles {
        return {
            type: AdministrationActionType.DoneUpsertingReportRoles,
        }
    }
}

export class ToggleImportBaseReportsModalVisibilityAction implements Redux.Action {
    type: AdministrationActionType.ToggleImportBaseReportsModalVisibility;

    static create(): ToggleImportBaseReportsModalVisibilityAction {
        return {
            type: AdministrationActionType.ToggleImportBaseReportsModalVisibility,
        }
    }
}

export class OtherAction implements Redux.Action {
    type: AdministrationActionType.OtherAction;

    static create(): OtherAction {
        return {
            type: AdministrationActionType.OtherAction,
        }
    }
}

export type AdministrationAction = ChangeViewAction | FailedToConnectToReportServerAction | ConnectedToReportServerAction | SetActiveReportAction | ToggleImportBaseReportsModalVisibilityAction |
    ReceiveCredentialsAction | ReceiveFoldersAction | ToggleReportServerConnectionVisibilityAction | ReceiveReportRolesAction | ToggleReportRolesVisiblityAction | DoneUpsertingReportRoles | OtherAction | SetSelectedRootFolderAction;
